import { useEffect, useRef, useState } from "react";
import { dealerApi } from "../utils/dealerApi";

/**
 * Encapsulates dealer notifications state + fetchers + handlers + the
 * 30s polling + outside-click dismissal of the notifications dropdown.
 *
 * Extracted from `pages/DealerPortal.js` (refactor 22 Feb 2026 — pure code move).
 * Caller: only `dealer` role uses this. Pass `enabled=false` for sellers.
 */
export const useDealerNotifications = ({ dealerToken, enabled }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const notifRef = useRef(null);

  const fetchNotifications = async (t) => {
    try {
      const res = await dealerApi.get(`/dealer/notifications`, { token: t || dealerToken });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch { /* silent — mirrors original behaviour */ }
  };

  const markNotificationRead = async (id) => {
    try {
      await dealerApi.put(`/dealer/notifications/${id}/read`, { token: dealerToken });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch { /* */ }
  };

  const markAllRead = async () => {
    try {
      await dealerApi.put(`/dealer/notifications/read-all`, { token: dealerToken });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch { /* */ }
  };

  // Poll every 30s only when active
  useEffect(() => {
    if (!dealerToken || !enabled) return undefined;
    const interval = setInterval(() => fetchNotifications(), 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealerToken, enabled]);

  // Outside-click closes the dropdown
  useEffect(() => {
    const handler = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifications(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return {
    notifications,
    unreadCount,
    showNotifications,
    setShowNotifications,
    notifRef,
    fetchNotifications,
    markNotificationRead,
    markAllRead,
  };
};
